import { toast } from 'react-toastify';
import errorHandle from '../../../../utils/errorHandle';
import fileUpload from '../../../../utils/fileUpload';
import deleteFile from '../../../../utils/deleteFile';
import { guidTourCreateFun, orgActiveGuidEditFun, orgInactiveGuidEditFun } from '../../../../api/orgGuidAPI';
import { validateTimeSlots } from '../../../../utils/packUploads/uploadValidators';

const check = (result, ref, scrollArg) => {
    if (!result.valid) {
        toast.error(result.message);
        ref?.current?.scrollToAndHighlightField(result.field ?? scrollArg ?? '');
        return false;
    }
    return true;
};

export const orgGuidPackUpSubmit = async (guidData, tempData, imgData, videoFile, isEdit, setLoading, ref = {}) => {
    if (!guidData.title || guidData.title.trim() === '') {
        toast.error('Title is required');
        ref.basicRef?.current?.scrollToAndHighlightField('title');
        return;
    }
    if (!guidData.overview || guidData.overview.trim() === '') {
        toast.error('Overview is required');
        ref.basicRef?.current?.scrollToAndHighlightField('overview');
        return;
    }
    if (!guidData.openTime || guidData.openTime.trim() === '') {
        toast.error('Opening Time is required');
        ref.basicRef?.current?.scrollToAndHighlightField('openTime');
        return;
    }
    if (!guidData.closeTime || guidData.closeTime.trim() === '') {
        toast.error('Closing Time is required');
        ref.basicRef?.current?.scrollToAndHighlightField('closeTime');
        return;
    }

    const quickView = guidData.quickView;

    if (!Array.isArray(quickView.type) || quickView.type.length === 0) {
        toast.error('Type must be a non-empty array.');
        ref.quickViewRef?.current?.scrollToAndHighlightField('type');
        return;
    }

    const hasInvalidTypeItems = quickView.type.some((item) => typeof item !== 'string' || item.trim() === '');

    if (hasInvalidTypeItems) {
        toast.error('Type contains empty or invalid items.');
        ref.quickViewRef?.current?.scrollToAndHighlightField('type');
        return;
    }

    const fieldsToValidate = ['difficulty', 'duration', 'trekDistance', 'baseCities', 'maxSlots', 'area'];

    for (const field of fieldsToValidate) {
        const value = quickView[field];

        const isEmptyString = typeof value === 'string' && value.trim() === '';
        const isInvalidStringValue = value === '-1';
        const isNullOrUndefined = value === null || value === undefined;
        const isEmptyArray = Array.isArray(value) && value.length === 0;

        if (isEmptyString || isInvalidStringValue || isNullOrUndefined || isEmptyArray) {
            const label = field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1');

            toast.error(`Quickview ${label} is required.`);
            ref.quickViewRef?.current?.scrollToAndHighlightField(field);
            return;
        }
    }

    if (!Array.isArray(quickView.languages) || quickView.languages.length === 0) {
        toast.error('Languages must be a non-empty array.');
        ref.quickViewRef?.current?.scrollToAndHighlightField('languages');
        return;
    }

    const hasLanguageTypeItems = quickView.languages.some((item) => typeof item !== 'string' || item.trim() === '');
    if (hasLanguageTypeItems) {
        toast.error('Languages contains empty or invalid items.');
        ref.quickViewRef?.current?.scrollToAndHighlightField('languages');
        return;
    }

    if (!Array.isArray(guidData.timeSlots) || guidData.timeSlots.length === 0) {
        toast.error('Please add at least one time slot');
        ref.addTimeSlotRef?.current?.scrollToAndHighlightField('timeSlots');
        return;
    }
    if (!check(validateTimeSlots(guidData), ref.timeSlotRef)) return;

    const places = guidData.places;

    if (!Array.isArray(places) || places.length === 0) {
        toast.error('Please add at least one place.');
        ref.placesRef?.current?.scrollToAndHighlightField('name');
        return;
    }

    for (let i = 0; i < places.length; i++) {
        const item = places[i];

        if (!item.name || item.name.trim() === '') {
            toast.error(`Name is required ${i + 1}`);
            ref.itineraryRef?.current?.scrollToAndHighlightField('name');
            return;
        }

        if (!item.description || item.description.trim() === '') {
            toast.error(`Description is required ${i + 1}`);
            ref.itineraryRef?.current?.scrollToAndHighlightField('description');
            return;
        }
    }

    if ((imgData?.oldWallImg?.length || 0) + (imgData?.newWallImg?.length || 0) < 5) {
        toast.error('Upload at least 5 Wallpaper Images');
        ref.imageRef?.current?.scrollToAndHighlightField('');
        return;
    }

    if ((imgData?.oldThumbImg?.length || 0) + (imgData?.newThumbImg?.length || 0) < 2) {
        toast.error('Upload at least 2 Thumbnail Images');
        ref.imageRef?.current?.scrollToAndHighlightField('');
        return;
    }
    if (20 > tempData.included.length || tempData.included.length > 800) {
        toast.error('Add atleast 2 includes');
        ref.inExRef?.current?.scrollToAndHighlightField('included');
        return;
    }
    if (20 > tempData.excluded.length || tempData.excluded.length > 800) {
        toast.error('Add atleast 2 excludes');
        ref.inExRef?.current?.scrollToAndHighlightField('excluded');
        return;
    }
    if (20 > tempData.safety.length || tempData.safety.length > 800) {
        toast.error('Add atleast 2 safety points');
        ref.safetyRef?.current?.scrollToAndHighlightField('safety');
        return;
    }

    if (20 > tempData.carry.length || tempData.carry.length > 800) {
        toast.error('Add atleast 2 things to carry points');
        ref.thingsRef?.current?.scrollToAndHighlightField('carry');
        return;
    }

    const FAQ = guidData.FAQ;
    for (let i = 0; i < FAQ.length; i++) {
        const faq = FAQ[i];

        if (typeof faq.question !== 'string' || faq.question.trim() === '') {
            toast.error(`Faq ${i + 1} question is required.`);
            ref.faqRef?.current?.scrollToAndHighlightField(`question${i}`);
            return;
        }

        if (typeof faq.answer !== 'string' || faq.answer.trim() === '') {
            toast.error(`Faq ${i + 1} answer is required.`);
            ref.faqRef?.current?.scrollToAndHighlightField(`answer${i}`);
            return;
        }
    }

    if (20 > tempData.terms.length || tempData.terms.length > 1500) {
        toast.error('Add atleast 1 point to terms');
        ref.termsRef?.current?.scrollToAndHighlightField('terms');
        return;
    }

    const price = guidData.price;

    if (!Array.isArray(price) || price.length === 0) {
        toast.error('Please add at least one price.');
        ref.priceRef?.current?.scrollToAndHighlightField('price');
        return;
    }

    for (let i = 0; i < price.length; i++) {
        const priceItem = price[i];

        if (priceItem.actual === null || isNaN(priceItem.actual)) {
            toast.error(`Price ${i + 1} actual price is required.`);
            ref.priceRef?.current?.scrollToAndHighlightField(`actual${i}`);
            return;
        }

        if (priceItem.reserve === null || isNaN(priceItem.reserve)) {
            toast.error(`Price ${i + 1} reserve amount is required.`);
            ref.priceRef?.current?.scrollToAndHighlightField(`reserve${i}`);
            return;
        }

        if (priceItem.perPersonPrice === null || isNaN(priceItem.perPersonPrice)) {
            toast.error(`Price ${i + 1} per person amount is required.`);
            ref.priceRef?.current?.scrollToAndHighlightField(`perPersonPrice${i}`);
            return;
        }

        if (!priceItem.name || priceItem.name.trim() === '') {
            toast.error(`Price ${i + 1} name is required.`);
            ref.priceRef?.current?.scrollToAndHighlightField(`name${i}`);
            return;
        }

        // if (!priceItem.terms || priceItem.terms.trim() === '') {
        //     toast.error(`Price ${i + 1} terms are required.`);
        //     ref.priceRef?.current?.scrollToAndHighlightField(`terms${i}`);
        //     return;
        // }
    }

    const checkTags = guidData.tags.every((item) => {
        return item.length > 50;
    });
    if (checkTags) {
        toast.error('One of the tags length is greater than 50');
        return;
    }
    setLoading(true);

    try {
        let data = {
            ...guidData,
            organizerId: localStorage.getItem('orgId'),
            organizerName: localStorage.getItem('orgName')
        };
        let type = localStorage.getItem('type');
        // Walpaper Image Upload
        if (isEdit) {
            const uploadedNewImgs = imgData?.newWallImg?.length
                ? await Promise.all(imgData.newWallImg.map((file) => fileUpload(file, type, 'create', 'wallImg')))
                : [];
            data.images = [...(imgData.oldWallImg || []), ...uploadedNewImgs];
        } else {
            data.images = await Promise.all(
                imgData.newWallImg.map((file) => fileUpload(file, type, 'create', 'wallImg'))
            );
        }

        // Thumbnail Image Upload
        if (isEdit) {
            const uploadedNewImgs = imgData?.newThumbImg?.length
                ? await Promise.all(imgData.newThumbImg.map((file) => fileUpload(file, type, 'create', 'thumbImg')))
                : [];
            data.cardImages = [...(imgData.oldThumbImg || []), ...uploadedNewImgs];
        } else {
            data.cardImages = await Promise.all(
                imgData.newThumbImg.map((file) => fileUpload(file, type, 'create', 'thumbImg'))
            );
        }

        // Video upload
        if (isEdit) {
            if (videoFile instanceof File) {
                if (guidData?.videos?.length > 0) {
                    await deleteFile(guidData.videos[0]);
                }
                const videoURL = await fileUpload(videoFile, type, 'create', 'tripVideo');
                data.videos = [videoURL];
            } else if (
                (videoFile === null || videoFile === undefined || videoFile === '') &&
                guidData?.videos?.length > 0
            ) {
                await deleteFile(guidData.videos[0]);
                data.videos = [];
            } else {
                data.videos = guidData.videos || [];
            }
        } else {
            if (videoFile instanceof File) {
                const videoURL = await fileUpload(videoFile, type, 'create', 'tripVideo');
                data.videos = [videoURL];
            } else {
                data.videos = [];
            }
        }

        data.included = tempData.included.split('\n').filter((line) => line.trim() !== '');
        data.excluded = tempData.excluded.split('\n').filter((line) => line.trim() !== '');
        data.terms = tempData.terms.split('\n').filter((line) => line.trim() !== '');
        data.safety = tempData.safety.split('\n').filter((line) => line.trim() !== '');
        data.carry = tempData.carry.split('\n').filter((line) => line.trim() !== '');

        let response;
        if (isEdit) {
            const { status, title, overview, days, nights, itinerary, included, excluded, ...rest } = data;
            if (data.status === 'active') {
                response = await orgActiveGuidEditFun(data._id, rest);
            } else {
                response = await orgInactiveGuidEditFun(data._id, data);
            }
        } else {
            const { _id, ...rest } = data;
            response = await guidTourCreateFun(rest);
        }
        if (response.status === 200) {
            toast.success(`Guide Package ${isEdit ? 'updated' : 'created'} successfully`);
            setLoading(false);
            return response.status;
        }
    } catch (error) {
        setLoading(false);
        errorHandle(error);
    }
};
